/**
 * Typed API client for the corpus Edge Function.
 * Auth is handled by corpusAPIClient — no token threading at call sites.
 */

import { corpusAPIClient } from "./apiClients";
import type { OffsetBasedPage } from "@shared/types/pagination.ts";
import { appendOffsetParams, type PageResponse } from "./pagination";
import { CORPUS_FEED_STATISTICS_MAX_IDS, SUBJECT_NAMES_MAX_IDS } from "@shared/types/corpus/api.ts";
import type {
  CorpusCatalogueFields,
  CorpusCatalogueSubjectPage,
  CloseCoverageGapBody,
  CoverageGap,
  CoverageGapMember,
  CoverageGapRecheckItemResult,
  CoverageGapRequester,
  CoverageGapRecheckOutcome,
  CoverageGapRecheckSummary,
  CoverageGapSort,
  CoverageGapState,
  CreateCoverageGapBody,
  UpdateCoverageGapBody,
  UncoveredClaim,
  UncoveredClaimSort,
} from "@shared/types/corpus/api.ts";
import type {
  CorpusFeed,
  CorpusFeedComposition,
  CorpusFeedRunResult,
  CorpusFeedStatisticsByFeed,
  CreateCorpusFeedParams,
  UpdateCorpusFeedParams,
  SubjectNamesBySubject,
  TaxonomyGapReview,
  TaxonomyGapStatusResponse,
} from "@shared/types/corpus/api.ts";
import type {
  TaxonomyGapEntry,
  TaxonomyGapKind,
  TaxonomyGapStatus,
  TaxonomyUrn,
} from "@shared/types/taxonomy.ts";
import type { ScorecardKey } from "@shared/scorecard";
import type { AdmissionCode, RejectionFinding } from "@shared/types/rejections.ts";
import type {
  SourceStatus,
  DimensionReviewState,
  AnnotationDimension,
  AnnotationMap,
  DimensionPreAnnotation,
  ExtractedScorecardWithEvidence,
  CandidateDraft,
  FieldCandidateDrafts,
  PrevalidatedField,
  MonoScorecardPayload,
  MultiScorecardConsensus,
  MultiScorecardConsensusField,
  MultiScorecardPayload,
  ScorecardKind,
  ScorecardPayload,
  QueueItem,
  QueueRow,
  QueueListResponse,
  CreateDocumentApiResponse,
  IngestResult,
  ReExtractResult,
  DocumentStats,
  TierClearedStatus,
  BacklogFieldCount,
  BacklogFieldsResponse,
  BacklogNextResponse,
  BacklogWorkItem,
  WorkQueueAction,
  WorkQueueCronJob,
  WorkQueueJob,
  WorkQueueResponse,
  MissingFieldCountResponse,
  AnnotateResponse,
  ExtractionJobsStatusResponse,
  SupportedSourceConnector,
  DocumentExtractionTraceResponse,
  DocumentConnectorAttempt,
  DocumentSectionClassification,
  DocumentAdmissionTrace,
  DocumentAdmissionAxisAnswer,
  DocumentAdmissionFindingAssessment,
  DocumentAdmissionCompatibilityAssessment,
  RejectedItem,
  RejectedListResponse,
  AdmissionCheckResponse,
  BatchJob,
  BatchJobKind,
  BatchJobStatus,
  BatchJobItem,
  BatchJobItemStatus,
  BatchJobItemReview,
  BatchJobListResponse,
  BatchJobItemListResponse,
  RetryFailedBatchJobItemsResponse,
  AdmissionRecheckParams,
  AdmissionRecheckScope,
  AdmissionRecheckItemResult,
  AdmissionRecheckOutcome,
  AdmissionRecheckSummary,
  PubmedIngestParams,
  PubmedIngestOutcome,
  PubmedIngestReviewReason,
  PubmedIngestItemResult,
  PubmedIngestSummary,
  PubmedQueryPreview,
  FieldReannotationParams,
  FieldAnnotationState,
  FieldReannotationField,
  FieldReannotationScope,
  FieldReannotationOutcome,
  FieldReannotationHumanDecision,
  FieldReannotationItemResult,
  FieldReannotationSummary,
  DocumentIndexingRefreshParams,
  DocumentIndexingRefreshMode,
  DocumentIndexingRefreshOutcome,
  DocumentIndexingRefreshItemResult,
  DocumentIndexingRefreshSummary,
  DocumentSectionsRefreshParams,
  DocumentSectionsRefreshOutcome,
  DocumentSectionsRefreshItemResult,
  DocumentSectionsRefreshSummary,
  MissingSectionFilesResponse,
} from "@shared/types/corpus";
import { ScorecardPayloadView } from "@shared/types/corpus";

export type {
  CoverageGapRecheckItemResult,
  CoverageGapRecheckOutcome,
  CoverageGapRecheckSummary,
  RejectionFinding,
  ScorecardKey,
  SourceStatus,
  DimensionReviewState,
  AnnotationDimension,
  AnnotationMap,
  DimensionPreAnnotation,
  ExtractedScorecardWithEvidence,
  CandidateDraft,
  FieldCandidateDrafts,
  PrevalidatedField,
  MonoScorecardPayload,
  MultiScorecardConsensus,
  MultiScorecardConsensusField,
  MultiScorecardPayload,
  ScorecardKind,
  ScorecardPayload,
  QueueItem,
  QueueRow,
  DocumentStats,
  TierClearedStatus,
  BacklogFieldCount,
  BacklogWorkItem,
  WorkQueueAction,
  WorkQueueCronJob,
  WorkQueueJob,
  WorkQueueResponse,
  MissingFieldCountResponse,
  AnnotateResponse,
  SupportedSourceConnector,
  DocumentExtractionTraceResponse,
  DocumentConnectorAttempt,
  DocumentSectionClassification,
  DocumentAdmissionTrace,
  DocumentAdmissionAxisAnswer,
  DocumentAdmissionFindingAssessment,
  DocumentAdmissionCompatibilityAssessment,
  RejectedItem,
  RejectedListResponse,
  AdmissionCheckResponse,
  BatchJob,
  BatchJobKind,
  BatchJobStatus,
  BatchJobItem,
  BatchJobItemStatus,
  BatchJobItemReview,
  BatchJobListResponse,
  BatchJobItemListResponse,
  AdmissionRecheckParams,
  AdmissionRecheckScope,
  AdmissionRecheckItemResult,
  AdmissionRecheckOutcome,
  AdmissionRecheckSummary,
  PubmedIngestParams,
  PubmedIngestOutcome,
  PubmedIngestReviewReason,
  PubmedIngestItemResult,
  PubmedIngestSummary,
  PubmedQueryPreview,
  FieldReannotationParams,
  FieldAnnotationState,
  FieldReannotationField,
  FieldReannotationScope,
  FieldReannotationOutcome,
  FieldReannotationHumanDecision,
  FieldReannotationItemResult,
  FieldReannotationSummary,
  DocumentIndexingRefreshParams,
  DocumentIndexingRefreshMode,
  DocumentIndexingRefreshOutcome,
  DocumentIndexingRefreshItemResult,
  DocumentIndexingRefreshSummary,
  DocumentSectionsRefreshParams,
  DocumentSectionsRefreshOutcome,
  DocumentSectionsRefreshItemResult,
  DocumentSectionsRefreshSummary,
  MissingSectionFilesResponse,
};
export { ScorecardPayloadView };

// ── Scorecard helpers ─────────────────────────────────────────────────────────

/** Extract the mono ExtractedScorecardWithEvidence from a payload; null for multi or absent. */
export function getMonoScorecard(
  payload: ScorecardPayload | null
): ExtractedScorecardWithEvidence | null {
  return new ScorecardPayloadView(payload).monoScorecard;
}

/** Labels the AI suggested for one field on a mono scorecard; [] for multi, null, or no key. */
export function getMonoAiLabels(
  payload: ScorecardPayload | null,
  key: ScorecardKey | null | undefined
): string[] {
  if (!key) return [];
  return new ScorecardPayloadView(payload).monoScorecard?.[key]?.labels ?? [];
}

/** The merged mono scorecard to score and display on, projected from either payload kind (a multi
 *  payload is collapsed via ScorecardPayloadView.effectiveScorecard); null for an absent payload.
 *  Use this — not getMonoScorecard — wherever a single per-field view is needed regardless of kind
 *  (e.g. Improve Ranking), so multi-candidate documents aren't treated as empty. */
export function getEffectiveScorecard(
  payload: ScorecardPayload | null
): ExtractedScorecardWithEvidence | null {
  if (!payload) return null;
  return new ScorecardPayloadView(payload).effectiveScorecard;
}

/** The merged AI-suggested labels for one field, from either payload kind; [] for an absent
 *  payload or no key. The effectiveScorecard counterpart of getMonoAiLabels. */
export function getEffectiveAiLabels(
  payload: ScorecardPayload | null,
  key: ScorecardKey | null | undefined
): string[] {
  if (!key || !payload) return [];
  return new ScorecardPayloadView(payload).effectiveScorecard[key]?.labels ?? [];
}

/** The pipeline's pre-validated (fast-path) settlement for one field on a multi payload, or null
 *  when the field wasn't fast-pathed (or the payload is mono/absent). */
export function getPrevalidatedField(
  payload: ScorecardPayload | null,
  key: ScorecardKey | null | undefined
): PrevalidatedField | null {
  if (!key) return null;
  return new ScorecardPayloadView(payload).prevalidated[key] ?? null;
}

// ── View models (platform-only, not API shapes) ───────────────────────────────

/** Grouped override-mode interaction — state and callbacks for the label-picker overlay.
 *  Returned by annotation hooks; passed directly as the `override` prop on both panel components. */
export interface OverrideInteraction {
  mode: boolean;
  focusIndex: number;
  selection: string[];
  onFocusChange: (i: number) => void;
  onSelectionChange: (selection: string[]) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

/** View model consumed by SubmitUrlsDialog — one entry per submitted URL, covering only the
 *  submit call itself. "submitting" — POST /documents in flight. "submitted" — spawned as a
 *  Tangrowth job; extraction_jobs already has a durable row for it server-side, which finishes
 *  without this dialog.
 *  "duplicate" — URL is already ingested in the corpus; no-op. "rejected" — URL was previously
 *  rejected at corpus admission. "error" — the submit call itself failed. */
export type SubmitUrlResult =
  | { url: string; state: "submitting" }
  | { url: string; state: "submitted"; id: string; jobId: string }
  | { url: string; state: "duplicate"; id: string }
  | { url: string; state: "rejected"; rejection: RejectionFinding }
  | { url: string; state: "error"; error: string };

// ── API calls ─────────────────────────────────────────────────────────────────

/** Submits one URL for extraction. `interactive` picks the backend extraction path (see
 *  useSubmitUrls / createDocumentFromUrl in _shared): `true` runs Tangrowth's synchronous
 *  extraction inline in an edge background task (fast — no Modal queue tail — for a small,
 *  human-attended batch); `false` spawns an async Tangrowth job. Either way the call returns a
 *  jobId immediately and extraction_jobs durably tracks it. */
export async function createDocumentFromUrl(
  url: string,
  interactive: boolean
): Promise<CreateDocumentApiResponse> {
  const { data } = await corpusAPIClient.post<CreateDocumentApiResponse>(
    "/documents",
    { url },
    { params: { method: "url", interactive } }
  );
  return data;
}

/** Whether the extraction backend is warm right now — checking this also wakes it if not. Call
 *  while a form that needs it is visible, not app-wide (see useExtractionWarmup). */
export async function getExtractionJobsStatus(): Promise<ExtractionJobsStatusResponse> {
  const { data } =
    await corpusAPIClient.get<ExtractionJobsStatusResponse>("/extraction-jobs/status");
  return data;
}

export interface GetDocumentsParams extends OffsetBasedPage {
  status?: "all" | SourceStatus;
  /** Text filter applied server-side to document title and URL (case-insensitive substring). */
  q?: string;
  /** Health field URNs: documents tagged with any of them. */
  fields?: TaxonomyUrn[];
  /** Only the documents this corpus feed returned. */
  feedId?: string;
  /** Only the documents with this subject (`mesh:<descriptor UI>`) among their main subjects. */
  subject?: string;
}

export async function getDocuments(params: GetDocumentsParams = {}): Promise<QueueListResponse> {
  const { status = "all", limit = 25, offset = 0, q, fields = [], feedId, subject } = params;
  const { data } = await corpusAPIClient.get<QueueListResponse>("/documents", {
    params: {
      status,
      limit,
      offset,
      ...(q ? { q } : {}),
      ...(fields.length > 0 ? { fields: fields.join(",") } : {}),
      ...(feedId ? { feedId } : {}),
      ...(subject ? { subject } : {}),
    },
  });
  return data;
}

/** The corpus' ingested papers by health field. */
export async function getCorpusCatalogueFields(): Promise<CorpusCatalogueFields> {
  const { data } = await corpusAPIClient.get<CorpusCatalogueFields>("/catalogue/fields");
  return data;
}

export interface GetCorpusCatalogueSubjectsParams extends OffsetBasedPage {
  /** Only the papers tagged with this health field. */
  field?: TaxonomyUrn;
  /** Text found in the subject's name or id, in any case. */
  q?: string;
}

/** One page of the corpus' main subjects with their paper counts, the one with the most papers first. */
export async function getCorpusCatalogueSubjects(
  params: GetCorpusCatalogueSubjectsParams
): Promise<CorpusCatalogueSubjectPage> {
  const { field, q, ...page } = params;
  const qs = new URLSearchParams();
  appendOffsetParams(qs, page);
  if (field) qs.set("field", field);
  if (q) qs.set("q", q);
  const { data } = await corpusAPIClient.get<CorpusCatalogueSubjectPage>("/catalogue/subjects", {
    params: qs,
  });
  return data;
}

export interface GetCoverageGapsParams extends OffsetBasedPage {
  /** What to rank by first; the other figures break its ties. */
  sort?: CoverageGapSort;
  /** Open gaps (the default) or closed ones. */
  state?: CoverageGapState;
  /** Keeps only the gaps with people still to be told. */
  toTell?: boolean;
}

/** The open coverage gaps ranked by demand, or the closed ones, the most recently closed first. */
export async function getCoverageGaps(
  params: GetCoverageGapsParams = {}
): Promise<PageResponse<CoverageGap>> {
  const { sort = "askers", state = "open", toTell = false, ...page } = params;
  const qs = new URLSearchParams({ sort, state });
  if (toTell) qs.set("toTell", "true");
  appendOffsetParams(qs, page);
  const { data } = await corpusAPIClient.get<PageResponse<CoverageGap>>("/coverage-gaps", {
    params: qs,
  });
  return data;
}

/** One coverage gap, with its demand read live. */
export async function getCoverageGap(id: string): Promise<CoverageGap> {
  const { data } = await corpusAPIClient.get<CoverageGap>(`/coverage-gaps/${id}`);
  return data;
}

/** Creates a coverage gap with no claims. */
export async function createCoverageGap(body: CreateCoverageGapBody): Promise<CoverageGap> {
  const { data } = await corpusAPIClient.post<CoverageGap>("/coverage-gaps", body);
  return data;
}

/** Renames or re-describes a coverage gap. */
export async function updateCoverageGap(
  id: string,
  changes: UpdateCoverageGapBody
): Promise<CoverageGap> {
  const { data } = await corpusAPIClient.patch<CoverageGap>(`/coverage-gaps/${id}`, changes);
  return data;
}

/** The claims of a coverage gap, the most recently asked first. */
export async function getCoverageGapClaims(
  gapId: string,
  page: OffsetBasedPage
): Promise<PageResponse<CoverageGapMember>> {
  const qs = new URLSearchParams();
  appendOffsetParams(qs, page);
  const { data } = await corpusAPIClient.get<PageResponse<CoverageGapMember>>(
    `/coverage-gaps/${gapId}/claims`,
    { params: qs }
  );
  return data;
}

/** The uncovered claims in no coverage gap yet, ranked by `sort`. */
export async function getUngroupedUncoveredClaims(
  sort: UncoveredClaimSort,
  page: OffsetBasedPage
): Promise<PageResponse<UncoveredClaim>> {
  const qs = new URLSearchParams({ sort });
  appendOffsetParams(qs, page);
  const { data } = await corpusAPIClient.get<PageResponse<UncoveredClaim>>(
    "/ungrouped-uncovered-claims",
    { params: qs }
  );
  return data;
}

/** The people to tell about a closed coverage gap's claims, those not yet told first. */
export async function getCoverageGapRequesters(
  gapId: string,
  page: OffsetBasedPage = {}
): Promise<PageResponse<CoverageGapRequester>> {
  const qs = new URLSearchParams();
  appendOffsetParams(qs, page);
  const { data } = await corpusAPIClient.get<PageResponse<CoverageGapRequester>>(
    `/coverage-gaps/${gapId}/requesters`,
    { params: qs }
  );
  return data;
}

/** Records that a person who asked was told, or was not. */
export async function markCoverageGapRequesterTold(
  gapId: string,
  requestId: string,
  told: boolean
): Promise<void> {
  const path = `/coverage-gaps/${gapId}/requesters/${requestId}/told`;
  if (told) await corpusAPIClient.put(path);
  else await corpusAPIClient.delete(path);
}

/** Closes an open coverage gap; closing as filled starts the check of its claims. */
export async function closeCoverageGap(
  gapId: string,
  body: CloseCoverageGapBody
): Promise<CoverageGap> {
  const { data } = await corpusAPIClient.post<CoverageGap>(`/coverage-gaps/${gapId}/closure`, body);
  return data;
}

/** Opens a closed coverage gap again. */
export async function reopenCoverageGap(gapId: string): Promise<CoverageGap> {
  const { data } = await corpusAPIClient.delete<CoverageGap>(`/coverage-gaps/${gapId}/closure`);
  return data;
}

/** Puts a claim in a coverage gap, moving it out of the one it was in. */
export async function attachClaimToCoverageGap(gapId: string, claimId: string): Promise<void> {
  await corpusAPIClient.put(`/coverage-gaps/${gapId}/claims/${claimId}`);
}

/** Takes a claim out of a coverage gap, back to the claims not yet grouped. */
export async function detachClaimFromCoverageGap(gapId: string, claimId: string): Promise<void> {
  await corpusAPIClient.delete(`/coverage-gaps/${gapId}/claims/${claimId}`);
}

/** Every corpus feed, active, paused and retired, for choosing one (the API returns at most 200). */
export async function listCorpusFeeds(): Promise<CorpusFeed[]> {
  const { data } = await corpusAPIClient.get<PageResponse<CorpusFeed>>("/feeds", {
    params: { limit: 200, offset: 0, includeRetired: true },
  });
  return data.items;
}

export interface GetCorpusFeedPageParams extends OffsetBasedPage {
  /** Text filter on feed name and description (case-insensitive substring). */
  q?: string;
}

/** One page of corpus feeds as the Library lists them: active first, then paused. Retired feeds
 *  are left out. */
export async function getCorpusFeedPage(
  page: GetCorpusFeedPageParams = {}
): Promise<PageResponse<CorpusFeed>> {
  const { data } = await corpusAPIClient.get<PageResponse<CorpusFeed>>("/feeds", {
    params: {
      limit: page.limit ?? 50,
      offset: page.offset ?? 0,
      ...(page.q ? { q: page.q } : {}),
    },
  });
  return data;
}

/** One corpus feed. */
export async function getCorpusFeed(id: string): Promise<CorpusFeed> {
  const { data } = await corpusAPIClient.get<CorpusFeed>(`/feeds/${id}`);
  return data;
}

/** Renames, re-describes, pauses, resumes or retires a corpus feed; returns it as it now is. */
export async function updateCorpusFeed(
  id: string,
  changes: UpdateCorpusFeedParams
): Promise<CorpusFeed> {
  const { data } = await corpusAPIClient.patch<CorpusFeed>(`/feeds/${id}`, changes);
  return data;
}

/** Creates a corpus feed from a query whose preview was just seen. It does not run until its
 *  schedule comes round or an admin runs it. platform.admin only. */
export async function createCorpusFeed(params: CreateCorpusFeedParams): Promise<CorpusFeed> {
  const { data } = await corpusAPIClient.post<CorpusFeed>("/feeds", params);
  return data;
}

/** Starts a run of a corpus feed now: a job over what its query has found that is new to it. */
export async function startCorpusFeedRun(id: string): Promise<CorpusFeedRunResult> {
  const { data } = await corpusAPIClient.post<CorpusFeedRunResult>(`/feeds/${id}/runs`);
  return data;
}

/** What a corpus feed's papers are made of: those in the corpus, by health field and by subject. */
export async function getCorpusFeedComposition(id: string): Promise<CorpusFeedComposition> {
  const { data } = await corpusAPIClient.get<CorpusFeedComposition>(`/feeds/${id}/composition`);
  return data;
}

/** One page of a corpus feed's runs, newest first, as batch jobs. */
export async function listCorpusFeedRuns(
  id: string,
  page: OffsetBasedPage = {}
): Promise<BatchJobListResponse> {
  const { data } = await corpusAPIClient.get<BatchJobListResponse>(`/feeds/${id}/runs`, {
    params: { limit: page.limit ?? 20, offset: page.offset ?? 0 },
  });
  return data;
}

/** What each of the corpus feeds has brought in, by feed id. Asks for as many feeds as the API
 *  answers for at once, and so makes more than one request only for a very long list. */
export async function getCorpusFeedStatistics(
  feedIds: string[]
): Promise<CorpusFeedStatisticsByFeed> {
  const statistics: CorpusFeedStatisticsByFeed = {};
  for (let start = 0; start < feedIds.length; start += CORPUS_FEED_STATISTICS_MAX_IDS) {
    const ids = feedIds.slice(start, start + CORPUS_FEED_STATISTICS_MAX_IDS);
    const { data } = await corpusAPIClient.get<CorpusFeedStatisticsByFeed>("/feeds/statistics", {
      params: { ids: ids.join(",") },
    });
    Object.assign(statistics, data);
  }
  return statistics;
}

/** The readable name of each MeSH subject (`mesh:<descriptor UI>`), by subject; one with no known
 *  name is left out. Asks for as many subjects as the API answers for at once, and so makes more
 *  than one request only for a very long list. */
export async function getSubjectNames(subjects: string[]): Promise<SubjectNamesBySubject> {
  const names: SubjectNamesBySubject = {};
  for (let start = 0; start < subjects.length; start += SUBJECT_NAMES_MAX_IDS) {
    const ids = subjects.slice(start, start + SUBJECT_NAMES_MAX_IDS);
    const { data } = await corpusAPIClient.get<SubjectNamesBySubject>("/subjects/names", {
      params: { ids: ids.join(",") },
    });
    Object.assign(names, data);
  }
  return names;
}

export async function getDocument(id: string): Promise<QueueRow> {
  const { data } = await corpusAPIClient.get<QueueRow>(`/documents/${id}`);
  return data;
}

export async function getDocumentMarkdown(id: string): Promise<string> {
  const { data } = await corpusAPIClient.get<{ markdown_text: string }>(
    `/documents/${id}/markdown`
  );
  return data.markdown_text;
}

/** Debug/observability read for the Investigations panel — how Tangrowth resolved this
 *  document. Null when no trace was ever recorded (predates migration 031). */
export async function getDocumentExtractionTrace(
  id: string
): Promise<DocumentExtractionTraceResponse> {
  const { data } = await corpusAPIClient.get<DocumentExtractionTraceResponse>(
    `/documents/${id}/extraction-trace`
  );
  return data;
}

export async function saveAnnotation(
  id: string,
  annotation: Record<string, AnnotationDimension>,
  metadata: Record<string, unknown>
): Promise<AnnotateResponse> {
  const { data } = await corpusAPIClient.post<AnnotateResponse>(`/documents/${id}/annotate`, {
    annotation,
    metadata,
  });
  return data;
}

export async function rejectDocument(
  id: string,
  code: AdmissionCode,
  message: string
): Promise<void> {
  await corpusAPIClient.post(`/documents/${id}/reject`, { code, message });
}

export async function ingestDocument(id: string): Promise<void> {
  await corpusAPIClient.post<IngestResult>(`/documents/${id}/ingest`, {});
}

/** Re-runs extraction for the given scorecard fields on one document. Never changes a human
 *  decision; where the AI now disagrees with one, it is listed in `contradictions`. */
export async function reExtractDocument(
  id: string,
  fields: ScorecardKey[]
): Promise<ReExtractResult> {
  const { data } = await corpusAPIClient.post<ReExtractResult>(`/documents/${id}/re-extract`, {
    fields,
  });
  return data;
}

export async function getBacklogFields(): Promise<BacklogFieldsResponse> {
  const { data } = await corpusAPIClient.get<BacklogFieldsResponse>("/backlog/fields");
  return data;
}

export async function getNextBacklogItem(field?: ScorecardKey): Promise<BacklogWorkItem | null> {
  const { data } = await corpusAPIClient.get<BacklogNextResponse>("/backlog/next", {
    params: field ? { field } : undefined,
  });
  return data.item;
}

/** What the signed-in user has to do next, limited to what their roles can act on. */
export async function getWorkQueue(): Promise<WorkQueueResponse> {
  const { data } = await corpusAPIClient.get<WorkQueueResponse>("/work-queue");
  return data;
}

/** Counts the pending, ready and ingested documents with no label for one scorecard field. Reads
 *  every scorecard, so call it on demand. platform.admin only. */
export async function getMissingFieldCount(
  field: FieldReannotationField
): Promise<MissingFieldCountResponse> {
  const { data } = await corpusAPIClient.get<MissingFieldCountResponse>(
    "/documents/missing-field-count",
    { params: { field } }
  );
  return data;
}

/** Counts the documents whose section files are missing from Storage. Checks every document, so
 *  call it on demand. platform.admin only. */
export async function getMissingSectionFileCounts(): Promise<MissingSectionFilesResponse> {
  const { data } = await corpusAPIClient.get<MissingSectionFilesResponse>(
    "/documents/missing-section-files"
  );
  return data;
}

/** Ingested documents sorted by rank_stability ascending — Improve Ranking (TAN-37, deferred). */
export async function getImproveRankingCandidates(
  params: OffsetBasedPage = {}
): Promise<QueueListResponse> {
  const { limit = 25, offset = 0 } = params;
  const { data } = await corpusAPIClient.get<QueueListResponse>("/documents", {
    params: { sort: "rank_stability", limit, offset },
  });
  return data;
}

/** Document counts by status. */
export async function getDocumentStats(): Promise<DocumentStats> {
  const { data } = await corpusAPIClient.get<DocumentStats>("/documents/stats");
  return data;
}

/** Paginated list of URLs permanently rejected by the corpus admission gate. */
export async function listRejectedDocuments(
  params: OffsetBasedPage = {}
): Promise<RejectedListResponse> {
  const qs = new URLSearchParams();
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.offset !== undefined) qs.set("offset", String(params.offset));
  const { data } = await corpusAPIClient.get<RejectedListResponse>(
    `/rejected${qs.toString() ? `?${qs}` : ""}`
  );
  return data;
}

// ── Taxonomy gaps ─────────────────────────────────────────────────────────────

/** A page of the vocabulary entries documents lacked and that are not yet filled, escalated first.
 *  Filtered by status and kind when given. */
export async function listTaxonomyGaps(
  params: OffsetBasedPage & {
    status?: Exclude<TaxonomyGapStatus, "filled">;
    kind?: TaxonomyGapKind;
  } = {}
): Promise<PageResponse<TaxonomyGapReview>> {
  const qs = new URLSearchParams();
  appendOffsetParams(qs, params);
  if (params.status) qs.set("status", params.status);
  if (params.kind) qs.set("kind", params.kind);
  const { data } = await corpusAPIClient.get<PageResponse<TaxonomyGapReview>>(
    `/taxonomy-gaps${qs.toString() ? `?${qs}` : ""}`
  );
  return data;
}

/** Decides a gap by hand: the entry the triage then inserts into the vocabulary. */
export async function decideTaxonomyGap(
  id: string,
  entry: TaxonomyGapEntry
): Promise<TaxonomyGapStatusResponse> {
  const { data } = await corpusAPIClient.post<TaxonomyGapStatusResponse>(
    `/taxonomy-gaps/${id}/decision`,
    { entry }
  );
  return data;
}

/** Sends a gap back to the judge, with no decision. */
export async function reopenTaxonomyGap(id: string): Promise<TaxonomyGapStatusResponse> {
  const { data } = await corpusAPIClient.post<TaxonomyGapStatusResponse>(
    `/taxonomy-gaps/${id}/reopen`,
    {}
  );
  return data;
}

// ── Batch jobs ────────────────────────────────────────────────────────────────

/** Starts an admission_recheck batch job over the given scope. platform.admin only. */
export async function startAdmissionRecheck(params: AdmissionRecheckParams): Promise<BatchJob> {
  const { data } = await corpusAPIClient.post<BatchJob>("/batch-jobs/admission-rechecks", params);
  return data;
}

/** Checks a PubMed Central query before a job or a feed is created from it: how many articles it
 *  matches and how NCBI read it, with the title and link of the first `sampleSize` matches. Rejects
 *  with NCBI's reason when it refuses the query outright. platform.admin only. */
export async function previewPubmedIngestQuery(
  query: string,
  sampleSize = 0
): Promise<PubmedQueryPreview> {
  const { data } = await corpusAPIClient.get<PubmedQueryPreview>(
    "/batch-jobs/pubmed-ingests/preview",
    { params: { query, sampleSize } }
  );
  return data;
}

/** Starts a pubmed_ingest batch job over the PMC articles a query matches. platform.admin only. */
export async function startPubmedIngest(params: PubmedIngestParams): Promise<BatchJob> {
  const { data } = await corpusAPIClient.post<BatchJob>("/batch-jobs/pubmed-ingests", params);
  return data;
}

/** Starts a field_reannotation batch job re-annotating one scorecard field over a scope of
 *  documents. platform.admin only. */
export async function startFieldReannotation(params: FieldReannotationParams): Promise<BatchJob> {
  const { data } = await corpusAPIClient.post<BatchJob>("/batch-jobs/field-reannotations", params);
  return data;
}

/** Starts a document_sections_refresh batch job fetching again and re-embedding the documents whose section
 *  files are missing from Storage. platform.admin only. */
export async function startDocumentSectionsRefresh(
  params: DocumentSectionsRefreshParams
): Promise<BatchJob> {
  const { data } = await corpusAPIClient.post<BatchJob>(
    "/batch-jobs/document-sections-refreshes",
    params
  );
  return data;
}

/** Starts a document_indexing_refresh batch job refreshing the indexes of documents (fields, subjects, main subjects, publication types), over every document or only the untagged ones.
 *  platform.admin only. */
export async function startDocumentIndexingRefresh(
  params: DocumentIndexingRefreshParams
): Promise<BatchJob> {
  const { data } = await corpusAPIClient.post<BatchJob>(
    "/batch-jobs/document-indexing-refreshes",
    params
  );
  return data;
}

export interface ListBatchJobsParams extends OffsetBasedPage {
  /** An ISO timestamp: only the jobs created at or after it. Left out, jobs of every age. */
  createdAfter?: string;
}

export async function listBatchJobs(
  params: ListBatchJobsParams = {}
): Promise<BatchJobListResponse> {
  const { limit = 25, offset = 0, createdAfter } = params;
  const { data } = await corpusAPIClient.get<BatchJobListResponse>("/batch-jobs", {
    params: { limit, offset, createdAfter },
  });
  return data;
}

export async function getBatchJob(id: string): Promise<BatchJob> {
  const { data } = await corpusAPIClient.get<BatchJob>(`/batch-jobs/${id}`);
  return data;
}

/** Filters on the items of an admission_recheck or pubmed_ingest job, whose results carry a
 *  rejection. */
export interface RejectionItemFilters {
  /** Narrows to items whose result.rejection.code matches. */
  rejectionCode?: AdmissionCode;
}

/** Filters on the items of a field_reannotation job. */
export interface FieldReannotationItemFilters {
  /** Narrows to items whose AI labels before the re-annotation include this label. */
  oldLabel?: string;
  /** Narrows to items whose AI labels after the re-annotation include this label. */
  newLabel?: string;
}

export interface ListBatchJobItemsParams extends OffsetBasedPage {
  status?: BatchJobItemStatus;
  review?: BatchJobItemReview;
  /** Narrows to one processItem outcome — kind-specific (e.g. admission_recheck's
   *  AdmissionRecheckItemResult["outcome"], pubmed_ingest's PubmedIngestOutcome), passed through as
   *  a plain query param. */
  outcome?: string;
  /** Filters specific to one kind of job sit in an object named after it, and go over the wire
   *  namespaced the same way (`field_reannotation.old_label`). */
  admissionRecheck?: RejectionItemFilters;
  pubmedIngest?: RejectionItemFilters;
  fieldReannotation?: FieldReannotationItemFilters;
}

export async function listBatchJobItems(
  id: string,
  params: ListBatchJobItemsParams = {}
): Promise<BatchJobItemListResponse> {
  const {
    limit = 25,
    offset = 0,
    status,
    review,
    outcome,
    admissionRecheck,
    pubmedIngest,
    fieldReannotation,
  } = params;
  const { data } = await corpusAPIClient.get<BatchJobItemListResponse>(`/batch-jobs/${id}/items`, {
    params: {
      limit,
      offset,
      ...(status ? { status } : {}),
      ...(review ? { review } : {}),
      ...(outcome ? { outcome } : {}),
      ...(admissionRecheck?.rejectionCode
        ? { "admission_recheck.rejection_code": admissionRecheck.rejectionCode }
        : {}),
      ...(pubmedIngest?.rejectionCode
        ? { "pubmed_ingest.rejection_code": pubmedIngest.rejectionCode }
        : {}),
      ...(fieldReannotation?.oldLabel
        ? { "field_reannotation.old_label": fieldReannotation.oldLabel }
        : {}),
      ...(fieldReannotation?.newLabel
        ? { "field_reannotation.new_label": fieldReannotation.newLabel }
        : {}),
    },
  });
  return data;
}

export async function cancelBatchJob(id: string): Promise<void> {
  await corpusAPIClient.post(`/batch-jobs/${id}/cancel`, {});
}

/** Pauses a queued or running batch job, so the next queued job of its kind takes over. */
export async function pauseBatchJob(id: string): Promise<void> {
  await corpusAPIClient.post(`/batch-jobs/${id}/pause`, {});
}

/** Resumes a paused batch job: it queues again and runs once no other job of its kind is running. */
export async function resumeBatchJob(id: string): Promise<void> {
  await corpusAPIClient.post(`/batch-jobs/${id}/resume`, {});
}

/** Deletes (soft) a finished batch job, taking it out of the job list. platform.admin only. */
export async function deleteBatchJob(id: string): Promise<void> {
  await corpusAPIClient.delete(`/batch-jobs/${id}`);
}

export async function applyBatchJobItem(jobId: string, itemId: string): Promise<void> {
  await corpusAPIClient.post(`/batch-jobs/${jobId}/items/${itemId}/apply`, {});
}

export async function dismissBatchJobItem(jobId: string, itemId: string): Promise<void> {
  await corpusAPIClient.post(`/batch-jobs/${jobId}/items/${itemId}/dismiss`, {});
}

/** Puts a failed item back in line to be processed again. platform.admin only. */
export async function retryBatchJobItem(jobId: string, itemId: string): Promise<void> {
  await corpusAPIClient.post(`/batch-jobs/${jobId}/items/${itemId}/retry`, {});
}

/** Puts every failed item of a job back in line and returns how many there were. platform.admin only. */
export async function retryFailedBatchJobItems(jobId: string): Promise<number> {
  const { data } = await corpusAPIClient.post<RetryFailedBatchJobItemsResponse>(
    `/batch-jobs/${jobId}/retry`,
    {}
  );
  return data.retriedCount;
}

/** Re-runs the corpus admission gate against a document's current content, outside any batch job —
 *  used by the document detail dialog's "Re-check admission" action. */
export async function checkDocumentAdmission(id: string): Promise<AdmissionCheckResponse> {
  const { data } = await corpusAPIClient.post<AdmissionCheckResponse>(
    `/documents/${id}/admission-check`,
    {}
  );
  return data;
}
